import { useState } from "react";
import { useProbabilityAudit, type AuditRow } from "../../../../hooks/useProbabilityAudit";
import { motion } from "framer-motion";
import { Activity, Loader2, TrendingDown, TrendingUp, HelpCircle } from "lucide-react";

interface ProbabilityAuditProps {
  orgId?: string;
  prizeData: any;
}

const WINDOWS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 60 days", days: 60 },
  { label: "Last 90 days", days: 90 },
];

const SIG_BADGE: Record<string, { text: string; cls: string }> = {
  "very-low": { text: "Very low — not reliable", cls: "bg-destructive text-destructive-foreground" },
  "low": { text: "Low — directional only", cls: "bg-warning/20 text-warning border border-warning/40" },
  "moderate": { text: "Moderate", cls: "bg-primary/15 text-primary border border-primary/30" },
  "high": { text: "High — reliable", cls: "bg-accent/15 text-accent border border-accent/30" },
};

export function ProbabilityAudit({ orgId,prizeData }: ProbabilityAuditProps) {
  const [days, setDays] = useState(30);
  console.log(days, 'daysdaysdaysdays')
  const { data, loading } = useProbabilityAudit(orgId, prizeData, days);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const sig = SIG_BADGE[data.significance];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            Configured vs Observed Win Rate
          </h3>
          <p className="text-xs text-muted-foreground">
            Compare each prize's configured probability against how often it actually won.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm font-display"
        >
          {WINDOWS.map((w) => (
            <option key={w.days} value={w.days}>{w.label}</option>
          ))}
        </select>
      </div>

      {/* Summary */}
      <div className="glass rounded-xl p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <p className="text-xs font-display text-muted-foreground">Total spins in window</p>
          <p className="font-display text-2xl font-bold mt-1">{data.totalSpins}</p>
        </div>
        <div>
          <p className="text-xs font-display text-muted-foreground">Active prizes</p>
          <p className="font-display text-2xl font-bold mt-1">{data.activePrizes}</p>
        </div>
        <div>
          <p className="text-xs font-display text-muted-foreground flex items-center gap-1">
            Statistical significance <HelpCircle className="h-3 w-3" />
          </p>
          <span className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-display font-semibold ${sig.cls}`}>
            {sig.text}
          </span>
        </div>
      </div>

      {/* Per-prize */}
      <div className="glass rounded-xl p-5 space-y-6">
        <h4 className="font-display text-base font-semibold">Per-Prize Breakdown</h4>
        {data.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active prizes yet.</p>
        ) : (
          data.rows.map((r, i) => <Row key={r.id} row={r} index={i} />)
        )}
      </div>
    </div>
  );
}

function Row({ row, index }: { row: AuditRow; index: number }) {
  const color = row.color ?? "#6949df";
  const max = Math.max(row.configured, row.observed, 1);
  console.log(row, 'rowrowrow')
  const driftIcon =
    row.drift >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />;
  const driftColor = row.status === "on-target"
    ? "text-muted-foreground"
    : row.drift >= 0
    ? "text-accent"
    : "text-warning";
  const statusBadge =
    row.status === "on-target"
      ? { text: "On target", cls: "bg-accent/15 text-accent" }
      : row.status === "over"
      ? { text: "Over", cls: "bg-primary/15 text-primary" }
      : { text: "Under", cls: "bg-warning/15 text-warning" };

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      className="space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
          <span className="font-display text-sm font-semibold">{row.name}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-display font-semibold ${statusBadge.cls}`}>
            {statusBadge.text}
          </span>
        </div>
        <div className={`flex items-center gap-1 text-xs font-display ${driftColor}`}>
          {driftIcon}
          {row.drift >= 0 ? "+" : ""}
          {row.drift.toFixed(1)} pts
        </div>
      </div>
      {/* Bar with configured (faded) vs observed (solid) */}
      <div className="relative h-3 rounded-full bg-muted overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 opacity-30"
          style={{ width: `${(row.configured / max) * 100}%`, backgroundColor: color }}
        />
        <div
          className="absolute inset-y-0 left-0"
          style={{ width: `${(row.observed / max) * 100}%`, backgroundColor: color }}
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-display">
        <div>
          <p className="text-muted-foreground">Configured</p>
          <p className="font-semibold mt-0.5">{row.configured.toFixed(1)}%</p>
        </div>
        <div>
          <p className="text-muted-foreground">Observed</p>
          <p className="font-semibold mt-0.5">{row.observed.toFixed(1)}%</p>
        </div>
        <div>
          <p className="text-muted-foreground">Wins / Expected</p>
          <p className="font-semibold mt-0.5">{row.wins} / {row.expected.toFixed(1)}</p>
        </div>
        <div>
          <p className="text-muted-foreground">95% CI</p>
          <p className="font-semibold mt-0.5">
            {row.ciLow.toFixed(1)}% – {row.ciHigh.toFixed(1)}%
          </p>
        </div>
      </div>
    </motion.div>
  );
}
